import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { globalAttributes } from '../../common/global-attributes';
import { useOriAliases } from '../../common/hooks/use-ori-aliases';
import { useLocationPrefill, UseLocationPrefillArgs } from '../../location-select/use-location-prefill';
import {
    useEventOriginPrefill,
    useOriAliasPrefill,
    usePriorityPrefill,
    useReportingPartyLocationPrefill,
    useRiskLevelPrefill
} from '../../common/hooks/use-form-prefill-utils';

export function useEventPanelFormPrefill(form: UseFormReturn<EventPanelFormType>): void {
    const bootstrapState = useBootstrapState();
    const oriAliasOptions = useOriAliases();
    // we assert here because this mounts in the event window and should always exist in that context
    const eventPanelOptions = bootstrapState.eventPanelOptions as EventPanelOptions;
    const policeLocationPrefillArgs: UseLocationPrefillArgs<EventPanelFormType> = useMemo(() => {
        const args: UseLocationPrefillArgs<EventPanelFormType> = {
            form,
            agencyType: globalAttributes.agencyTypeGlobal.POLICE,
            fieldPaths: {
                location: 'location',
                agency: 'policeAgency',
                dispatchArea: 'policeDispatchArea',
                radioChannel: 'policePrimaryRadioChannel',
                subdivision1: 'policeSubdivision1',
                subdivision2: 'policeSubdivision2',
                subdivision3: 'policeSubdivision3',
                subdivision4: 'policeSubdivision4',
                subdivision5: 'policeSubdivision5',
            },
            options: {
                dispatchAreaOptions: eventPanelOptions.policeDispatchAreaOptions,
                agencyProfileOptions: eventPanelOptions.policeAgencyProfileOptions,
                radioChannelOptions: eventPanelOptions.radioChannelOptions,
            },
        };
        return args;
    }, [
        eventPanelOptions.policeAgencyProfileOptions,
        eventPanelOptions.policeDispatchAreaOptions,
        eventPanelOptions.radioChannelOptions,
        form,
    ]);

    const fireLocationPrefillArgs: UseLocationPrefillArgs<EventPanelFormType> = useMemo(() => {
        const args: UseLocationPrefillArgs<EventPanelFormType> = {
            form,
            agencyType: globalAttributes.agencyTypeGlobal.FIRE,
            fieldPaths: {
                location: 'location',
                agency: 'fireAgency',
                dispatchArea: 'fireDispatchArea',
                radioChannel: 'firePrimaryRadioChannel',
                subdivision1: 'fireSubdivision1',
                subdivision2: 'fireSubdivision2',
                subdivision3: 'fireSubdivision3',
                subdivision4: 'fireSubdivision4',
                subdivision5: 'fireSubdivision5',
            },
            options: {
                dispatchAreaOptions: eventPanelOptions.fireDispatchAreaOptions,
                agencyProfileOptions: eventPanelOptions.fireAgencyProfileOptions,
                radioChannelOptions: eventPanelOptions.radioChannelOptions,
            },
        };
        return args;
    }, [
        eventPanelOptions.fireAgencyProfileOptions,
        eventPanelOptions.fireDispatchAreaOptions,
        eventPanelOptions.radioChannelOptions,
        form,
    ]);

    const emsLocationPrefillArgs: UseLocationPrefillArgs<EventPanelFormType> = useMemo(() => {
        const args: UseLocationPrefillArgs<EventPanelFormType> = {
            form,
            agencyType: globalAttributes.agencyTypeGlobal.EMS,
            fieldPaths: {
                location: 'location',
                agency: 'emsAgency',
                dispatchArea: 'emsDispatchArea',
                radioChannel: 'emsPrimaryRadioChannel',
                subdivision1: 'emsSubdivision1',
                subdivision2: 'emsSubdivision2',
                subdivision3: 'emsSubdivision3',
                subdivision4: 'emsSubdivision4',
                subdivision5: 'emsSubdivision5',
            },
            options: {
                dispatchAreaOptions: eventPanelOptions.emsDispatchAreaOptions,
                agencyProfileOptions: eventPanelOptions.emsAgencyProfileOptions,
                radioChannelOptions: eventPanelOptions.radioChannelOptions,
            },
        };
        return args;
    }, [
        eventPanelOptions.emsAgencyProfileOptions,
        eventPanelOptions.emsDispatchAreaOptions,
        eventPanelOptions.radioChannelOptions,
        form,
    ]);

    useLocationPrefill<EventPanelFormType>(policeLocationPrefillArgs);
    useLocationPrefill<EventPanelFormType>(fireLocationPrefillArgs);
    useLocationPrefill<EventPanelFormType>(emsLocationPrefillArgs);

    const riskLevelOptions = eventPanelOptions.riskLevelOptions ?? [];
    const priorityOptions = bootstrapState.eventPanelOptions?.priorityOptions ?? [];
    const eventOriginOptions = eventPanelOptions.eventOriginOptions ?? [];

    useRiskLevelPrefill(riskLevelOptions);
    usePriorityPrefill(priorityOptions);
    useEventOriginPrefill(eventOriginOptions);
    useReportingPartyLocationPrefill();
    useOriAliasPrefill(oriAliasOptions);
}
